import { sendGAEvent, sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';

import type { Placement } from 'src/constants/recommendations';
import { getPersonalisationBreakdown } from 'src/redux/personalisation/helpers/selectorHelpers';
import type { PersonalisationState } from 'src/redux/personalisation/types';
import { generateProductsForRecommendationsEvent } from 'src/utils/googleAnalytics';
import type { ListingsCarouselProduct } from '../../../../adapters/listingsAPIToProductCarouselAdapter';
import type {
  ProductClickEvent,
  ProductImpressionsInitialEvent,
  ProductShelfImpressionEvent,
  ProductShelfNavigationImpression,
} from './types';

const eventCategory = 'Listings Recommendations';

interface TrackRecommendedProductsImpression {
  personalisation: PersonalisationState;
  placementStrategy: RecommendationsServingConfig;
  placementType: Placement;
  products: ListingsCarouselProduct[] | null;
}

export const trackRecommendedProductsImpression = ({
  personalisation,
  placementStrategy,
  placementType,
  products,
}: TrackRecommendedProductsImpression) => {
  if (!products) {
    return;
  }
  sendGAEvent<ProductImpressionsInitialEvent>({
    ecommerce: null,
  });

  const eventLabel = `${placementStrategy || ''} : ${placementType}`;

  const { amountAdded: addedPersonalisationCost, amountRemoved: removedPersonalisationCost } =
    getPersonalisationBreakdown(personalisation.selectedPersonalisations, 'GBP');

  sendGAEvent<ProductShelfImpressionEvent>({
    event: 'product_impressions',
    event_category: eventCategory,
    event_action: 'Product Impressions',
    event_label: eventLabel,
    ecommerce: {
      impressions: generateProductsForRecommendationsEvent({
        products: products,
        placementName: placementType,
        placementStrategy,
        personalisationsTotalAmount: addedPersonalisationCost - removedPersonalisationCost,
      }),
    },
  });
};

interface TrackRecommendedProductClick {
  personalisation: PersonalisationState;
  placementStrategy: RecommendationsServingConfig;
  placementType: Placement;
  product: ListingsCarouselProduct | null;
  productIndex: number;
}
export const trackRecommendedProductClick = (
  e: React.MouseEvent,
  {
    personalisation,
    placementStrategy,
    placementType,
    product,
    productIndex,
  }: TrackRecommendedProductClick,
) => {
  if (!product) {
    return;
  }
  sendGAEvent<ProductImpressionsInitialEvent>({
    ecommerce: null,
  });

  const positionInList = productIndex + 1;
  const eventLabel = `${positionInList} : ${placementStrategy || ''} : ${placementType}`;

  const { amountAdded: addedPersonalisationCost, amountRemoved: removedPersonalisationCost } =
    getPersonalisationBreakdown(personalisation.selectedPersonalisations, 'GBP');

  sendGALinkClickEvent<ProductClickEvent>(e, {
    event: 'product_clicks',
    event_category: eventCategory,
    event_action: 'Product Click',
    event_label: eventLabel,
    ecommerce: {
      click: {
        actionField: {
          list: `product - listings recommendations - ${placementType} - ${placementStrategy}`,
        },
        products: generateProductsForRecommendationsEvent({
          products: [product!],
          placementName: placementType,
          placementStrategy,
          positionInList,
          personalisationsTotalAmount: addedPersonalisationCost - removedPersonalisationCost,
        }),
      },
    },
  });
};

interface TrackRecommendedProductsCarouselNavigation {
  navigation: {
    direction: 'Left' | 'Right';
    type: 'Arrow Click' | 'Scroll';
  };
  placementStrategy: RecommendationsServingConfig;
  placementType: Placement;
}

export const trackRecommendedProductsCarouselNavigation = ({
  navigation,
  placementStrategy,
  placementType,
}: TrackRecommendedProductsCarouselNavigation) => {
  const eventLabel = `${placementStrategy} : ${placementType}`;
  const { direction, type } = navigation;

  const navigationType = type === 'Arrow Click' ? 'Arrow click' : 'Swipe';
  const navigationDirection = direction === 'Right' ? 'next' : 'previous';

  sendGAEvent<ProductShelfNavigationImpression>({
    event: 'custom_event',
    event_category: 'Listings Recommendations',
    event_action: `${navigationType} ${navigationDirection}`,
    event_label: eventLabel,
  });
};
